import { Modal, ModalButtons } from 'ui/Modal'
import { Button } from 'ui/components/Button'
import { useParams } from 'react-router-dom'
import { usePaymentMethodsForCustomer } from 'api/PaymentMethods'
import { useUpdateWearablePaymentMethod } from 'api/Customers'
import { useTranslation } from 'react-i18next'
import { Notification, useNotificationContext } from 'ui/components/Notification'
import { Select } from 'ui/components/Form/Select'
import { PaymentMethodIcon } from 'ui/components/PaymentMethodIcon'
import { Form, Formik } from 'formik'
import { PlusIcon } from '@heroicons/react/solid'
import { useState } from 'react'
import { SelectTerminalModal } from './SelectTerminalModal'

export const AssociatePaymentMethodModal = ({
  isOpen,
  onClose,
  barcodeId,
  initialPaymentMethodId,
}) => {
  const { t } = useTranslation()
  const { clientId } = useParams<{ clientId: string }>()
  const notification = useNotificationContext()
  const [openTerminalModal, setOpenTerminalModal] = useState(false)
  const {
    data: { data: paymentMethods } = {},
    isLoading: isLoadingPaymentMethods,
    refetch,
  } = usePaymentMethodsForCustomer({ customerId: clientId, forBarcode: true })
  const { mutate: updateWearablePaymentMethod, isLoading: isUpdating } =
    useUpdateWearablePaymentMethod()

  const paymentMethodOptions = paymentMethods?.map((paymentMethod) => ({
    value: paymentMethod?.id,
    name: (
      <div className="flex items-center space-x-2">
        <PaymentMethodIcon size={24} type={paymentMethod.card_brand} />
        <span>
          {paymentMethod.cardholder_name} •••• {paymentMethod.last_4} &middot; Exp{' '}
          {paymentMethod.card_expiry ?? '??/??'}
        </span>
      </div>
    ),
  }))

  const handleAssociate = (vals) => {
    updateWearablePaymentMethod(
      {
        customerId: clientId,
        barcodeId,
        updateWearablePaymentMethod: {
          payment_method_id: vals.payment_method_id,
        },
      },
      {
        onSuccess: () => {
          notification.notify(
            <Notification
              title={t('clients.client_profile.associate_payment_method_modal.notification.title')}
              description={t(
                'clients.client_profile.associate_payment_method_modal.notification.description'
              )}
              variant="success"
              autoDismiss
            />
          )
          onClose()
        },
        onError: (response: Response) => {
          notification.notifyApiError(response)
        },
      }
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      title={t('clients.client_profile.associate_payment_method_modal.title')}
      onClose={onClose}
      size="sm"
    >
      <Formik
        initialValues={{ payment_method_id: initialPaymentMethodId || '' }}
        onSubmit={handleAssociate}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <div className="mb-4">
              <Select
                label={t('clients.client_profile.associate_payment_method_modal.select')}
                options={paymentMethodOptions || []}
                selected={
                  paymentMethodOptions?.find((opt) => opt.value === values.payment_method_id) ||
                  null
                }
                onChange={(option) => setFieldValue('payment_method_id', option?.value || '')}
                disabled={isLoadingPaymentMethods}
                filterEnabled={false}
              />
            </div>

            <div className="mb-4 flex justify-start">
              <Button
                leadingIcon={PlusIcon}
                label={t('clients.client_profile.associate_payment_method_modal.add_new_button')}
                variant="secondary"
                onClick={() => {
                  setOpenTerminalModal(true)
                }}
              />
            </div>

            <SelectTerminalModal
              isOpen={openTerminalModal}
              onClose={() => setOpenTerminalModal(false)}
              customerId={clientId}
              onComplete={() => {
                setOpenTerminalModal(false)
                refetch()
              }}
              isAddPaymentMethod
            />

            <ModalButtons>
              <Button
                label={t('clients.client_profile.associate_payment_method_modal.associate_button')}
                variant="primary"
                type="submit"
                disabled={!values.payment_method_id || isUpdating}
                loading={isUpdating}
              />
              <Button label={t('modal_button.cancel')} onClick={onClose} />
            </ModalButtons>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}
